import { Alert, Button, Group, type AlertProps } from '@mantine/core'
import { IconAlertTriangle, IconArrowRight } from '@tabler/icons-react'
import type { ReactNode } from 'react'

export interface AttentionBannerProps extends Omit<
    AlertProps,
    'color' | 'variant' | 'icon' | 'title' | 'style'
> {
    title: ReactNode
    // Defaults to the warning triangle; override only for a meaningfully
    // different cue (e.g. a settings glyph for "finish setup").
    icon?: ReactNode
    // When set, renders an explicit action button (a real, labeled control —
    // not a whole-banner click target, which reads poorly to a screen reader).
    // Use for banners that describe something the user can act on directly.
    onAction?: () => void
    actionLabel?: ReactNode
}

// The shared warning notice. A warning-tinted Mantine Alert with a consistent
// icon + title, used for every "needs attention / heads up" message — the
// page-level banners at the top of the main pages AND in-context warnings inside
// modals and forms. Forwards Alert layout props (e.g. `mt`). For errors or info
// use a plain Alert with the matching semantic color, not this.
export function AttentionBanner({
    title,
    icon,
    onAction,
    actionLabel,
    children,
    ...others
}: AttentionBannerProps) {
    return (
        <Alert
            {...others}
            color='warning'
            variant='light'
            icon={icon ?? <IconAlertTriangle size={18} />}
            title={title}
            style={{ flexShrink: 0 }}
        >
            {children}
            {onAction && (
                <Group mt={children ? 'xs' : 4}>
                    <Button
                        variant='light'
                        color='warning'
                        size='compact-sm'
                        rightSection={<IconArrowRight size={14} />}
                        onClick={onAction}
                    >
                        {actionLabel}
                    </Button>
                </Group>
            )}
        </Alert>
    )
}
